<template>
  <teleport to="body">
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div id="huddle_invite_dialog" v-if="showInvite" class="fixed inset-0 z-[1001] flex items-center justify-center bg-black/40">
        <div
          class="rounded-2xl shadow-2xl w-[320px] flex flex-col items-center overflow-hidden huddle_invite_card"
        >
          <div
            class="w-full px-5 pt-5 pb-3 flex flex-col items-center gap-1"
          >
            <div class="flex items-center text-sm secondary_color">
              <span>Incoming Call</span>
            </div>
            <p class="text-base font-semibold text-center text_color">
              {{ groupName }}
            </p>
          </div>

          <div class="py-4 flex flex-col items-center gap-3">
            <div class="relative">
              <div class="huddle_caller_pulse" />
              <ProfileIcon :data="inviterUser" :size="72" :font="28" />
            </div>

            <div class="text-center">
              <p class="text-sm font-semibold text_color">
                {{ inviterName }}
              </p>
              <p class="text-xs secondary_color mt-0.5">
                {{ callStatusText }}
              </p>
            </div>

            <div v-if="otherParticipants.length > 0" class="flex items-center gap-1 mt-1">
              <div class="flex -space-x-2">
                <ProfileIcon
                  v-for="p in otherParticipants.slice(0, 3)"
                  :key="p.user_id"
                  :data="getParticipantUser(p)"
                  :size="24"
                  :font="9"
                  class="border-2 rounded-full huddle_avatar_border"
                />
              </div>
              <span class="text-[11px] secondary_color ml-1">
                {{ otherParticipantsText }}
              </span>
            </div>
          </div>

          <div class="w-full px-5 pb-5 flex justify-center gap-3">
            <a-button
                class="danger_medium"
              @click="handleCancel"
            >
              Decline
            </a-button>
            <a-button
                class="primary_medium"
              @click="handleJoin"
            >
              Join
            </a-button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { renderEntity } from "@/src/utils/utils.js";

const store = useStore();
const router = useRouter();

const incomingInvite = computed(() => store.getters["huddles/incomingInvite"]);
const isConnected = computed(() => store.getters["huddles/isConnected"]);
const allUsers = computed(() => store.getters["users/users"] || []);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);

const showInvite = computed(() => !!incomingInvite.value && !isConnected.value);

const inviterUser = computed(() => {
  if (!incomingInvite.value) return { name: "Unknown" };
  const user = allUsers.value.find(
    (u) => Number(u.id) === Number(incomingInvite.value.inviter_user_id)
  );
  return user || { id: incomingInvite.value.inviter_user_id, name: `User ${incomingInvite.value.inviter_user_id}` };
});

const inviterName = computed(() => renderEntity(inviterUser.value));

const groupName = computed(() => {
  if (!incomingInvite.value?.group_id) return "Huddle";
  const groups = store.getters["chats/groups"] || [];
  const group = groups.find(
    (g) => String(g.id) === String(incomingInvite.value.group_id)
  );
  return group?.name || "Huddle";
});

const otherParticipants = computed(() => {
  const participants = incomingInvite.value?.participants || [];
  return participants.filter(
    (p) =>
      Number(p.user_id) !== Number(incomingInvite.value?.inviter_user_id) &&
      Number(p.user_id) !== Number(currentUserId.value)
  );
});

const otherParticipantsText = computed(() => {
  const count = otherParticipants.value.length;
  if (count === 0) return "";
  if (count === 1) {
    const name = renderEntity(getParticipantUser(otherParticipants.value[0]));
    return `${name} is also in the call`;
  }
  return `+${count} others in the call`;
});

const callStatusText = computed(() => {
  const participants = incomingInvite.value?.participants || [];
  const total = participants.length;
  if (total <= 1) return "is calling you...";
  return `and ${total - 1} ${total - 1 === 1 ? "other" : "others"} are calling...`;
});

const getParticipantUser = (participant) => {
  const user = allUsers.value.find(
    (u) => Number(u.id) === Number(participant.user_id)
  );
  return user || { id: participant.user_id, name: `User ${participant.user_id}` };
};

const handleJoin = async () => {
  try {
    const invite = incomingInvite.value;
    store.dispatch("huddles/clearIncomingInvite");
    if (invite?.session_id) {
      // Navigate to chats page first if not already there
      const currentRoute = window.location.pathname;
      if (!currentRoute.includes("/projects/chats")) {
        if (invite.group_id) {
          const groups = store.getters["chats/groups"] || [];
          const group = groups.find((g) => String(g.id) === String(invite.group_id));
          if (group) {
            store.dispatch("chats/setActiveChat", group);
          }
        }
        await router.push({ name: "chats" });
      }

      // Then connect to the huddle
      await store.dispatch("huddles/joinHuddle", { sessionId: invite.session_id, groupId: invite.group_id });
    }
  } catch (error) {
    console.error("HuddleInviteDialog: failed to join huddle", error);
  }
};

const handleCancel = () => {
  try {
    store.dispatch("huddles/clearIncomingInvite");
  } catch (error) {
    console.error("HuddleInviteDialog: failed to cancel invite", error);
  }
};

// Auto-dismiss when the specific huddle we were invited to ends
const activeHuddle = computed(() => store.getters["huddles/activeHuddle"]);
watch(activeHuddle, (huddle, oldHuddle) => {
  if (!incomingInvite.value) return;
  // Only dismiss if the cleared huddle matches the invite's session
  if (!huddle && oldHuddle &&
      String(oldHuddle.session_id) === String(incomingInvite.value.session_id)) {
    store.dispatch("huddles/clearIncomingInvite");
  }
});
</script>

<style scoped>
.huddle_ring_anim {
  animation: huddle_ring 1.5s ease-in-out infinite;
}

@keyframes huddle_ring {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(15deg); }
  75% { transform: rotate(-15deg); }
}

.huddle_caller_pulse {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid var(--priority-tag-green-text);
  animation: huddle_caller_ring 2s ease-in-out infinite;
}

@keyframes huddle_caller_ring {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.1); }
}

.huddle_invite_card {
  background: var(--background-color);
  border: 1px solid var(--white-opacity-10);
}

.huddle_avatar_border {
  border-color: var(--background-color);
}
</style>
